/*
 * Copyright (c) Nexign, JSC, 2019-2020.
 */
/**
 *
 * @author Fedorov Maxim
 * @author Yakov Gushchin
 * @author Aleksandr Perkhunov
 * @version 1.2.0
 * @since 1.0.1
 */

import { createSelector, OutputSelector } from 'reselect';

import { IRootReducer } from '../../../../../reducers/rootReducer';
import { EWidgetStatus } from '../widgetTypes';
import {
    IWidgetSubscriberMobileItem,
    IWidgetSubscriberMobileReducer,
    IWidgetSubscriberMobileStatusSubs,
} from './widgetSubscriberMobileTypes';

export const selectWidgetSubscriberMobile = (state: IRootReducer): IWidgetSubscriberMobileReducer =>
    state.dashboard.widgetSubscriberMobile;

export const makeSelectWidgetSubscriberMobileIsLoading = ():
    OutputSelector<IRootReducer, boolean, (state: IWidgetSubscriberMobileReducer) => boolean> =>
    createSelector(
        selectWidgetSubscriberMobile,
        (state: IWidgetSubscriberMobileReducer) => state.loading,
    );

export const makeSelectWidgetSubscriberMobileGetLabels = ():
    OutputSelector<IRootReducer,
        IWidgetSubscriberMobileItem[],
        (state: IWidgetSubscriberMobileReducer) => IWidgetSubscriberMobileItem[]> =>
    createSelector(
        selectWidgetSubscriberMobile,
        (state: IWidgetSubscriberMobileReducer) => state.data.labels,
    );

export const makeSelectWidgetSubscriberMobileGetTariffs = ():
    OutputSelector<IRootReducer,
        IWidgetSubscriberMobileItem[],
        (state: IWidgetSubscriberMobileReducer) => IWidgetSubscriberMobileItem[]> =>
    createSelector(
        selectWidgetSubscriberMobile,
        (state: IWidgetSubscriberMobileReducer) => state.data.tariffs,
    );

export const makeSelectWidgetSubscriberMobileGetActiveSubs = ():
    OutputSelector<IRootReducer,
        IWidgetSubscriberMobileStatusSubs,
        (state: IWidgetSubscriberMobileReducer) => IWidgetSubscriberMobileStatusSubs> =>
    createSelector(
        selectWidgetSubscriberMobile,
        (state: IWidgetSubscriberMobileReducer) => state.data.activeSubs,
    );

export const makeSelectWidgetSubscriberMobileGetBlockedSubs = ():
    OutputSelector<IRootReducer,
        IWidgetSubscriberMobileStatusSubs,
        (state: IWidgetSubscriberMobileReducer) => IWidgetSubscriberMobileStatusSubs> =>
    createSelector(
        selectWidgetSubscriberMobile,
        (state: IWidgetSubscriberMobileReducer) => state.data.blockedSubs,
    );

export const makeSelectWidgetSubscriberMobileGetNoSimCardSubs = ():
    OutputSelector<IRootReducer,
        IWidgetSubscriberMobileStatusSubs,
        (state: IWidgetSubscriberMobileReducer) => IWidgetSubscriberMobileStatusSubs> =>
    createSelector(
        selectWidgetSubscriberMobile,
        (state: IWidgetSubscriberMobileReducer) => state.data.noSimCardSubs,
    );

export const makeSelectorLabelsStatus =
    (): OutputSelector<IRootReducer, EWidgetStatus, (state: IWidgetSubscriberMobileReducer) => EWidgetStatus> =>
        createSelector(
            selectWidgetSubscriberMobile,
            (state: IWidgetSubscriberMobileReducer) => state.error
                ? EWidgetStatus.ERROR
                : state.data.labels.length ? EWidgetStatus.SUCCESS : EWidgetStatus.EMPTY,
        );

export const makeSelectorTariffsStatus =
    (): OutputSelector<IRootReducer, EWidgetStatus, (state: IWidgetSubscriberMobileReducer) => EWidgetStatus> =>
        createSelector(
            selectWidgetSubscriberMobile,
            (state: IWidgetSubscriberMobileReducer) => state.error
                ? EWidgetStatus.ERROR
                : state.data.tariffs.length ? EWidgetStatus.SUCCESS : EWidgetStatus.EMPTY,
        );

